import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {groupBy, isEqual, times} from 'lodash';

import {Course, Store} from 'src/types/store';
import {classYears} from 'shared/portfolio/utils';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {
  courseFormFromCourse,
  courseGroupingKey,
  courseGroupingKeyFromInstitution,
  newCourseObject,
} from './utils';

import {CourseForm} from './course-form-wrapper';
import SchoolYearSection from './school-year-section';
import MobileSchoolYearSection from './mobile-school-year-section';

interface Props {
  year: string;
  coursesForYear: Course[];
}

const SchoolYear = ({year, coursesForYear}: Props) => {
  const mq = useMediaQuery();
  const [editing, setEditing] = useState(false);
  const graduationYear = useSelector((state: Store) => state.student.highSchoolInfo.graduationYear);
  const currentInstitution = useSelector((state: Store) => state.student.currentInstitution);
  const institutions = useSelector((state: Store) => state.institutions);
  const fourSchoolYears = useMemo(() => classYears(graduationYear), [graduationYear]);

  const coursesByGradesAndSchool: Record<string, Record<string, Course[]>> = useMemo(() => {
    const coursesForYearByInstitution = groupBy(coursesForYear, (course) => course.institution._id);
    const institutionsForYear = [
      institutions[currentInstitution._id], // always include current institution, and make sure it's first
      ...Object.values(institutions).filter(
        (institution) =>
          institution._id !== currentInstitution._id &&
          (institution.activeYears.length === 0 || institution.activeYears.includes(year))
      ),
    ];

    // Process institutions with courses first so they appear before the ones without courses
    const result = institutionsForYear
      .filter((institution) => coursesForYearByInstitution[institution._id])
      .reduce((acc, institution) => {
        const coursesByGradeSettings = groupBy(
          coursesForYearByInstitution[institution._id] || [],
          (course) => JSON.stringify(courseGroupingKey(course))
        );
        const institutionGradeSetting = JSON.stringify(
          courseGroupingKeyFromInstitution(institution)
        );

        acc[institution._id] = {
          // Make sure default grade setting from institution exists and is first, this also
          // ensures that the new grade setting section shows up when the user changes it
          [institutionGradeSetting]: coursesByGradeSettings[institutionGradeSetting] || [],
          ...coursesByGradeSettings,
        };

        return acc;
      }, {});

    // Process institutions without courses after so they appear after the ones with courses
    institutionsForYear
      .filter((institution) => !coursesForYearByInstitution[institution._id])
      .forEach((institution) => {
        result[institution._id] = {
          [JSON.stringify(courseGroupingKeyFromInstitution(institution))]: [],
        };
      });

    return result;
  }, [coursesForYear, institutions, year, currentInstitution]);

  const initialValues: Record<string, CourseForm[]> = useMemo(() => {
    const totalCoursesForYear = coursesForYear.length;
    const institutionIds = Object.keys(coursesByGradesAndSchool);
    const result = {};

    institutionIds.forEach((institutionId) => {
      const courseGroups = coursesByGradesAndSchool[institutionId];

      Object.keys(courseGroups).forEach((key) => {
        const {institutionId: _institutionId, ...gradeSettings} = JSON.parse(key);
        const courses = courseGroups[key];

        result[key] = [];

        if (courses.length) {
          courses.forEach((course) => {
            result[key].push(courseFormFromCourse(course));
          });
        }

        // If desktop bulk entry, automatically create up to 6 new courses for the
        // current institution for easy filling out
        if (mq.MD_AND_UP) {
          times(
            currentInstitution._id === institutionId &&
              totalCoursesForYear < 6 &&
              isEqual(gradeSettings, currentInstitution.gradeSettings)
              ? 6 - totalCoursesForYear
              : 1,
            () =>
              result[key].push({
                ...newCourseObject({
                  ...institutions[institutionId],
                  gradeSettings,
                }),
                schoolYear: year,
              })
          );
          // If mobile, only auto create a course if there is one school and no courses
        } else if (!totalCoursesForYear && institutionIds.length === 1 && mq.SM_AND_DOWN) {
          result[key].push({
            ...newCourseObject({
              ...institutions[institutionId],
              gradeSettings,
            }),
            schoolYear: year,
          });
        }
      });
    });

    return result;
  }, [
    coursesByGradesAndSchool,
    currentInstitution,
    institutions,
    year,
    coursesForYear.length,
    mq.MD_AND_UP,
    mq.SM_AND_DOWN,
  ]);

  return mq.MD_AND_UP ? (
    <SchoolYearSection
      editing={editing}
      setEditing={setEditing}
      classYear={fourSchoolYears[year]}
      year={year}
      initialValues={initialValues}
      coursesByGradesAndSchool={coursesByGradesAndSchool}
    />
  ) : (
    <MobileSchoolYearSection
      editing={editing}
      setEditing={setEditing}
      classYear={fourSchoolYears[year]}
      year={year}
      initialValues={initialValues}
      coursesByGradesAndSchool={coursesByGradesAndSchool}
    />
  );
};

export default SchoolYear;
